import {RankersNamespaceKey} from "../constants/rankers_namespace";
import {getRankersUserAgent} from "../constants/rankers_user_agent";

export const XHR_ACCEPT_HEADERS = 'text/javascript, text/html, application/xml, text/xml, */*';
export const XHR_REQUESTED_WITH_HEADERS = 'XMLHttpRequest';

/**
 * A class for fetching content via XHR. This is compatible with Rails' `format.js` response block and the `request.xhr?` method.
 * @param {string} url - The URL to fetch.
 * @param {object} options - The options to pass to the Request constructor.
 * @param {string} options.method - The HTTP method to use. Defaults to 'GET'.
 */
export class XHRFetcher {

    constructor(url, options = {}) {
        this.url = url;
        this.request = this.buildRequest(options);
        this.content = null;
    }

    buildRequest(options = {}) {
        options.method ??= 'GET';

        // Include cookies in the request to approved domains (based on the cookie's `domain` value).
        // The server also needs to respond to the preflight OPTIONS request with the `Access-Control-Allow-Credentials` header set to `true`.
        // See `CrossDomainAccess#set_cross_domain_access` in the Rails app where this is set.
        options.credentials ??= 'include';

        let request = new Request(this.url, options);

        // Note that with CORS the server must allow these headers to be set.
        // See the Rails controller module `CrossDomainAccess#set_cross_domain_access`.
        request.headers.set('Accept', XHR_ACCEPT_HEADERS); // Important! Ensures that the Rails `format.js` response block is run.
        request.headers.set('X-Requested-With', XHR_REQUESTED_WITH_HEADERS); // Important! Ensures that the Rails method `request.xhr?` returns true.

        // Set the Rankers user agent header. This is used to specify the client that is making the request.
        // Setting the header (rather than relying on the client's user agent) is necessary because the user agent is not always correctly set when making JS requests from a webview.
        // This is a failsafe so that the server can still identify the client even if the user agent is not set correctly.
        request.headers.set('X-Rankers-User-Agent', getRankersUserAgent());

        return request;
    }

    // The `fetch` method is async, so it returns a Promise that resolves to the text content of the response.
    async fetch() {
        return await fetch(this.request)
            .then(this.handleResponse.bind(this))
            .then(this.handleTextContent.bind(this))
            .catch(this.handleError.bind(this));
    }

    handleResponse(response) {
        if (!response.ok) throw new Error('[XHRFetcher] Network response was not OK');
        return response.text();
    }

    handleTextContent(content) {
        this.content = content;
        return this.content;
    }

    handleError(error) {
        console.error('[XHRFetcher] There has been a problem with your fetch operation:', error);
    }

}